import { useEffect, useState } from "react";
import { api } from "../lib/api";
import type { PublishedPage } from "../lib/types";
import type { Account } from "../lib/types";
import { exerciseMascot, isExerciseConfigValid, renderExercise } from "../exercises/registry";
import { ExerciseActivityBoundary } from "../exercises/ExerciseActivityBoundary";
import { ContentBlocks } from "./ContentBlocks";

export function ContentPageView({ account, slug, adaptiveAssignmentId, recommendationId }: { account: Account; slug: string; adaptiveAssignmentId?: string; recommendationId?: string }) {
  const [page, setPage] = useState<PublishedPage | null>(null);
  const [error, setError] = useState("");
  const [bookmarkMessage, setBookmarkMessage] = useState("");

  useEffect(() => {
    setPage(null);
    setError("");
    setBookmarkMessage("");
    api<PublishedPage>(`/content/pages/${encodeURIComponent(slug)}`)
      .then(setPage)
      .catch((caught) => setError(caught instanceof Error ? caught.message : "Die Seite konnte nicht geladen werden."));
  }, [slug]);

  if (error) return <div className="alert error">{error}</div>;
  if (!page) return <div className="loading-card">Inhalt wird geladen …</div>;

  const hasValidExerciseConfig = page.exerciseType
    ? isExerciseConfigValid(page.exerciseType, page.exerciseConfig)
    : false;

  if (page.type === "EXERCISE" && page.exerciseType && !hasValidExerciseConfig) {
    return <article className="content-card"><div className="alert error"><strong>Diese Übung kann gerade nicht geladen werden.</strong><p>Die veröffentlichte Aufgabenkonfiguration ist unvollständig oder ungültig. Bitte informiere die Administration.</p></div></article>;
  }

  const contentMascot = exerciseMascot(page.exerciseType);

  async function toggleBookmark() {
    const result = await api<{ bookmarked: boolean }>("/learning/bookmarks/toggle", {
      method: "POST",
      body: JSON.stringify({ contentPageId: page!.id }),
    });
    setBookmarkMessage(result.bookmarked ? "Zur Merkliste hinzugefügt." : "Aus der Merkliste entfernt.");
  }

  return (
    <article className="content-card">
      <header className="content-heading">
        <img className="content-mascot" src={contentMascot} alt="" />
        <span className={`page-kind ${page.type.toLowerCase()}`}>{page.type === "EXERCISE" ? "Übung" : "Lernseite"}</span>
        <h1>{page.title}</h1>
        {page.summary && <p className="lead">{page.summary}</p>}
        <div className="content-heading-actions">
          <button className="secondary small" onClick={() => toggleBookmark().catch((caught) => setBookmarkMessage(caught.message))}>Merken</button>
          {bookmarkMessage && <small>{bookmarkMessage}</small>}
        </div>
      </header>
      <ContentBlocks blocks={page.blocks} />
      {page.exerciseType && hasValidExerciseConfig && <ExerciseActivityBoundary key={`${page.id}:${adaptiveAssignmentId ?? ""}:${recommendationId ?? ""}`} pageId={page.id}>{renderExercise(page.exerciseType, {
        account,
        pageId: page.id,
        version: page.version,
        config: page.exerciseConfig,
        adaptiveAssignmentId,
        recommendationId,
      })}</ExerciseActivityBoundary>}
      <footer className="version-note">
        <span>Inhaltsversion {page.version}</span>
        {page.exerciseTechnicalVersion && <span>Technikversion {page.exerciseTechnicalLabel} {page.exerciseTechnicalVersion}</span>}
        <span>Plattform {page.platformVersion}</span>
      </footer>
    </article>
  );
}
